import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Flame, Coins, Star, Trophy, BookOpen, Sparkles, PencilLine, ClipboardCheck, Timer,
  LogOut, Medal, Target, ChevronRight, Footprints, Zap, Award, Lock, Globe2, Volume2, VolumeX, ShoppingBag, Map, MoreVertical,
  Calculator, Plus, Grid3x3, PieChart, Clock, ScrollText, Divide, Hash, Percent, Scale, Shapes, ListOrdered, Puzzle,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { ThemedShell, ThemedCard, ThemedProgress } from "../../components/child/ThemedShell";
import { ThemeSwitcher } from "../../components/child/ThemeSwitcher";
import { LevelPicker } from "../../components/child/LevelPicker";
import { EXERCISE_TYPES, MATH_EXERCISE_TYPES, DIFFICULTIES, THEMES, THEMES_BY_GROEP, SUBJECTS, AVATARS } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { sfx, isMuted, toggleMuted } from "../../lib/sound";

const ICONS = { BookOpen, Sparkles, PencilLine, ClipboardCheck, Timer, Calculator, Plus, Grid3x3, PieChart, Clock, ScrollText, Hash, Percent, Scale, Shapes, ListOrdered, Puzzle };
const BADGE_ICONS = { Footprints, Target, Flame, Zap, Star, Trophy, Coins, Award, Medal, Calculator, Grid3x3, PieChart, Divide, Sparkles, Globe2, Map };

export default function ChildDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, terms } = useTheme();
  const [data, setData] = useState(null);
  const [pickTheme, setPickTheme] = useState(false);
  const [activeTheme, setActiveTheme] = useState(null);
  const [subject, setSubject] = useState("lezen");
  const [levelEdit, setLevelEdit] = useState(false);
  const [soundOff, setSoundOff] = useState(isMuted());

  const load = () => api.get("/child/dashboard").then(({ data }) => { setData(data); setActiveTheme(data.interests?.[0] || "Dieren"); });
  useEffect(() => { load(); }, []);

  const start = (type) => navigate("/leerling/oefening", { state: { type, subject, theme: activeTheme || "Dieren", difficulty: data.levels?.[subject] } });

  const saveLevels = async (newLevels) => {
    try { await api.put("/profile", { levels: newLevels }); } catch {}
    setLevelEdit(false);
    load();
  };

  if (!data) return (
    <ThemedShell><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div></ThemedShell>
  );

  const isMath = subject === "rekenen";
  const sub = data.subjects[subject];
  const types = isMath ? MATH_EXERCISE_TYPES : EXERCISE_TYPES;
  const typeKeys = isMath
    ? (data.math_types || Object.keys(MATH_EXERCISE_TYPES)).filter((k) => MATH_EXERCISE_TYPES[k])
    : (data.reading_types || Object.keys(EXERCISE_TYPES)).filter((k) => EXERCISE_TYPES[k]);
  const lezenGroep = Number(String(data.levels?.lezen || "M5").replace(/[^0-9]/g, "")) || 5;
  const themeOptions = THEMES_BY_GROEP[lezenGroep] || THEMES;
  const rec = sub.recommendation;
  const RecIcon = ICONS[types[rec.type]?.icon] || BookOpen;
  const muted = { color: "var(--t-muted)" };
  const lbl = {
    recommended: isMath ? "Jouw volgende som" : terms.recommended,
    nextCta: isMath ? "Start de som!" : terms.nextCta,
    all: isMath ? "Alle rekenoefeningen" : terms.allExercises,
  };

  return (
    <ThemedShell>
      <ThemeSwitcher open={pickTheme} onOpenChange={setPickTheme} />
      {data.needs_onboarding && (
        <LevelPicker forced title={`Hoi ${data.name}! In welke groep zit je?`}
          subtitle="Zo maken we de oefeningen precies goed voor jou. Je kunt dit later altijd aanpassen."
          onSave={(c) => saveLevels({ lezen: c, rekenen: c })} />
      )}
      {levelEdit && (
        <LevelPicker title={`Niveau ${SUBJECTS[subject].label}`}
          subtitle="Pas het niveau voor dit vak aan."
          initial={data.levels?.[subject]}
          onSave={(c) => saveLevels({ ...data.levels, [subject]: c })}
          onCancel={() => setLevelEdit(false)} />
      )}

      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 85%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-5 h-16 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-2xl shrink-0">{theme.mascot}</span>
            <span className="font-heading font-extrabold text-lg themed-heading hidden sm:inline truncate">SlimAvontuur</span>
          </span>
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full shrink-0" style={{ background: "var(--t-surface2)" }} data-testid="streak-chip">
              <Flame className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse-soft" style={{ color: "var(--t-primary)" }} /><span className="font-heading font-extrabold text-sm sm:text-base">{data.streak}</span>
            </div>
            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full shrink-0" style={{ background: "var(--t-surface2)" }} data-testid="coins-chip">
              <span className="text-base sm:text-lg">{terms.coinIcon}</span><span className="font-heading font-extrabold text-sm sm:text-base">{data.coins}</span>
            </div>
            <button onClick={() => navigate("/leerling/wereldreis")} data-testid="open-wereldreis" className="flex items-center justify-center w-9 h-9 rounded-full text-base active:scale-95 transition shrink-0" style={{ background: "var(--t-surface2)" }} title="Wereldreis">
              🌍
            </button>
            <button onClick={() => navigate("/leerling/race")} data-testid="open-race" className="relative flex items-center justify-center w-9 h-9 rounded-full text-base active:scale-95 transition shrink-0" style={{ background: "var(--t-surface2)" }} title="Knikkerrace">
              🏎️
              {data.race_tickets > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-black" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }} data-testid="race-ticket-badge">{data.race_tickets}</span>}
            </button>
            <button onClick={() => navigate("/leerling/winkel")} data-testid="open-shop" className="flex items-center justify-center w-9 h-9 rounded-full active:scale-95 transition shrink-0" style={{ background: "var(--t-surface2)" }} title="Winkel">
              <ShoppingBag className="w-4 h-4" style={{ color: "var(--t-accent)" }} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-menu-btn" className="flex items-center justify-center w-9 h-9 rounded-full active:scale-95 transition shrink-0" style={{ background: "var(--t-surface2)" }} title="Menu">
                  <MoreVertical className="w-4 h-4" style={{ color: "var(--t-text)" }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2" style={{ background: "var(--t-surface)", borderColor: "var(--t-border)", color: "var(--t-text)" }}>
                <DropdownMenuItem data-testid="open-theme-switcher" onClick={() => setPickTheme(true)} className="font-bold cursor-pointer focus:bg-transparent" style={{ color: "var(--t-text)" }}>
                  <Globe2 className="w-4 h-4 mr-2" style={{ color: "var(--t-accent)" }} /> {theme.name}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="sound-toggle" onClick={() => { const m = toggleMuted(); setSoundOff(m); if (!m) sfx.coin(); }} className="font-bold cursor-pointer focus:bg-transparent" style={{ color: "var(--t-text)" }}>
                  {soundOff ? <VolumeX className="w-4 h-4 mr-2" style={{ color: "var(--t-muted)" }} /> : <Volume2 className="w-4 h-4 mr-2" style={{ color: "var(--t-accent)" }} />} {soundOff ? "Geluid aan" : "Geluid uit"}
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background: "var(--t-border)" }} />
                <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="font-bold cursor-pointer focus:bg-transparent" style={{ color: "var(--t-text)" }}>
                  <LogOut className="w-4 h-4 mr-2" style={{ color: "var(--t-muted)" }} /> Uitloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 pt-8 pb-16">
        {/* Greeting + level */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="relative">
              <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3 }} className="w-16 h-16 rounded-3xl flex items-center justify-center text-4xl" style={{ background: "var(--t-surface2)" }}>{theme.mascot}</motion.div>
              <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center text-lg border-2 shadow" style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }} title="Jouw avatar" data-testid="dash-avatar">{AVATARS[data.avatar] || "🦊"}</span>
            </div>
            <div>
              <h1 className="font-heading text-3xl font-black themed-heading">{terms.greeting}, {data.name}!</h1>
              <p className="font-semibold flex items-center gap-1.5 flex-wrap" style={muted}>
                <button onClick={() => setLevelEdit(true)} data-testid="adjust-level-btn" className="font-bold underline decoration-dotted underline-offset-2 active:scale-95" style={{ color: "var(--t-accent)" }}>
                  {DIFFICULTIES[data.levels?.[subject]] || "Kies je niveau"}
                </button>
                <span>· {theme.tagline}</span>
              </p>
            </div>
          </div>
          <ThemedCard className="p-4 min-w-[230px] cursor-pointer active:scale-[0.99] transition-transform" data-testid="level-card" onClick={() => navigate("/leerling/avontuur")}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading font-extrabold flex items-center gap-1.5"><Star className="w-4 h-4" style={{ color: "var(--t-accent)", fill: "var(--t-accent)" }} /> {terms.level} {data.level.level} · {data.level.name}</span>
              <Map className="w-4 h-4" style={{ color: "var(--t-accent)" }} />
            </div>
            <ThemedProgress value={data.level.pct} />
            <p className="text-xs font-semibold mt-1.5" style={muted}>{data.level.xp_to_next > 0 ? `Nog ${data.level.xp_to_next} ${terms.xp} tot het volgende ${terms.level.toLowerCase()}` : "Hoogste niveau!"} · Bekijk je avontuur →</p>
          </ThemedCard>
        </div>

        {/* Subject tiles */}
        <p className="font-heading font-bold mb-3 themed-heading">Kies je vak</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {Object.entries(SUBJECTS).map(([key, s]) => {
            const active = subject === key;
            const SIcon = ICONS[s.icon] || BookOpen;
            return (
              <button key={key} onClick={() => setSubject(key)} data-testid={`subject-${key}`}
                className="flex items-center gap-4 p-5 rounded-3xl border-2 transition-all active:scale-[0.99]"
                style={active ? { background: "var(--t-primary)", color: "var(--t-on-primary)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shrink-0" style={{ background: active ? "rgba(255,255,255,0.2)" : "var(--t-surface2)" }}>{s.emoji}</div>
                <div className="text-left flex-1">
                  <p className="font-heading text-xl font-extrabold flex items-center gap-2"><SIcon className="w-5 h-5" /> {s.label}</p>
                  <p className="text-sm font-semibold" style={active ? { opacity: 0.9 } : muted}>{s.desc} · {data.subjects[key].total_exercises} gedaan</p>
                </div>
                {active && <ChevronRight className="w-6 h-6" />}
              </button>
            );
          })}
        </div>

        {/* Wereldreis CTA */}
        <button onClick={() => navigate("/leerling/wereldreis")} data-testid="dash-wereldreis-card"
          className="w-full mb-8 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition-all text-left"
          style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shrink-0" style={{ background: "var(--t-surface2)" }}>🌍</div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-extrabold themed-heading">Wereldreis 🗺️</h3>
            <p className="text-sm font-semibold" style={muted}>Sleep vlaggen op de kaart, raad vlaggen en verzamel landen in je wereldpaspoort!</p>
          </div>
          <ChevronRight className="w-6 h-6" style={{ color: "var(--t-accent)" }} />
        </button>

        {/* Speelhoek CTA */}
        <button onClick={() => navigate("/leerling/speelhoek")} data-testid="dash-speelhoek-card"
          className="w-full mb-8 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition-all text-left"
          style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shrink-0" style={{ background: "var(--t-surface2)" }}>🕹️</div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-extrabold themed-heading">Speelhoek 🎮</h3>
            <p className="text-sm font-semibold" style={muted}>Codekraker, Vogelvlucht en Knikkerrace — speel solo of daag een vriend uit!</p>
          </div>
          <ChevronRight className="w-6 h-6" style={{ color: "var(--t-accent)" }} />
        </button>
        {data.tafeltoets_available && (
          <button onClick={() => navigate(data.rumi_unlocked ? "/leerling/rumi-feest" : "/leerling/tafeltoets")} data-testid="dash-tafeltoets-card"
            className="w-full mb-8 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition-all text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-primary)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shrink-0" style={{ background: "var(--t-surface2)" }}>{data.rumi_unlocked ? "🎉" : "🎯"}</div>
            <div className="flex-1">
              <h3 className="font-heading text-xl font-extrabold themed-heading">Tafeltoets {data.rumi_unlocked && "— Rumi vrijgespeeld!"}</h3>
              <p className="text-sm font-semibold" style={muted}>{data.rumi_unlocked ? "Bekijk het dansfeestje van Rumi opnieuw! 💜" : "Tafels 1, 2, 3, 4 en 10. Haal 100% en speel een geheime dans-held vrij!"}</p>
            </div>
            <ChevronRight className="w-6 h-6" style={{ color: "var(--t-primary)" }} />
          </button>
        )}

        {/* Recommended */}
        <motion.div key={subject} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl p-7 sm:p-9 mb-8" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-sm mb-3" style={{ background: "rgba(255,255,255,0.2)" }}><Target className="w-4 h-4" /> {lbl.recommended}</span>
              <h2 className="font-heading text-2xl sm:text-3xl font-black themed-heading">{types[rec.type]?.label}</h2>
              <p className="mt-1.5 font-medium opacity-90">Extra oefenen met <strong>{rec.skill_label}</strong>{isMath ? "" : ` · thema ${rec.theme}`}</p>
            </div>
            <div className="w-16 h-16 rounded-3xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}><RecIcon className="w-9 h-9" strokeWidth={2.5} /></div>
          </div>
          <Button onClick={() => start(rec.type)} className="relative mt-6 rounded-full font-bold text-lg py-6 px-8 active:scale-95 shadow-lg border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }} data-testid="start-recommended-btn">
            {lbl.nextCta} <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </motion.div>

        {/* Theme picker (reading content theme) */}
        {!isMath && (
          <div className="mb-8">
            <p className="font-heading font-bold mb-3 themed-heading">Kies een thema</p>
            <div className="flex flex-wrap gap-2">
              {themeOptions.map((t) => {
                const sel = activeTheme === t;
                return (
                  <button key={t} onClick={() => setActiveTheme(t)} data-testid={`theme-${t}`}
                    className="px-4 py-2 rounded-full font-bold text-sm transition-all active:scale-95 border-2"
                    style={sel ? { background: "var(--t-accent)", color: "var(--t-on-accent)", borderColor: "var(--t-accent)" } : { background: "var(--t-surface)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>{t}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tafel-sprint CTA (rekenen) */}
        {isMath && (
          <button onClick={() => navigate("/leerling/sprint")} data-testid="open-sprint"
            className="w-full mb-8 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition-all text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Zap className="w-8 h-8" strokeWidth={2.5} /></div>
            <div className="flex-1">
              <h3 className="font-heading text-xl font-extrabold themed-heading">Tafel-sprint ⚡</h3>
              <p className="text-sm font-semibold" style={muted}>Oefen je tafels tegen de klok — hoeveel haal jij?</p>
            </div>
            <ChevronRight className="w-6 h-6" style={{ color: "var(--t-accent)" }} />
          </button>
        )}

        {/* Exercise types */}
        <p className="font-heading font-bold mb-3 themed-heading">{lbl.all}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {typeKeys.map((key) => {
            const t = types[key];
            const Icon = ICONS[t.icon] || BookOpen;
            return (
              <button key={key} onClick={() => start(key)} data-testid={`type-${key}`}
                className="group rounded-3xl border-2 hover:-translate-y-1 transition-all p-5 text-left flex items-center gap-4" style={{ background: "var(--t-surface)", borderColor: "var(--t-border)" }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-surface2)" }}><Icon className="w-7 h-7" strokeWidth={2.5} style={{ color: "var(--t-accent)" }} /></div>
                <div><h3 className="font-heading font-bold">{t.label}</h3><p className="text-sm font-semibold" style={muted}>Start →</p></div>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Challenges */}
          <ThemedCard className="p-6">
            <h3 className="font-heading font-extrabold mb-4 flex items-center gap-2 themed-heading"><Zap className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> {terms.challenges}</h3>
            {["daily", "weekly"].map((k) => {
              const c = data.challenges[k];
              const pct = Math.round((c.current / c.target) * 100);
              return (
                <div key={k} className="mb-4 last:mb-0" data-testid={`challenge-${k}`}>
                  <div className="flex justify-between text-sm font-bold mb-1.5"><span>{c.label}</span><span style={muted}>{c.current}/{c.target}</span></div>
                  <ThemedProgress value={pct} fill={k === "daily" ? "var(--t-primary)" : "var(--t-accent)"} />
                </div>
              );
            })}
            <Button onClick={() => navigate("/leerling/scorebord")} className="w-full mt-4 rounded-full font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="leaderboard-link"><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> {terms.leaderboard}</Button>
          </ThemedCard>

          {/* Badges */}
          <ThemedCard className="p-6 lg:col-span-2">
            <h3 className="font-heading font-extrabold mb-4 flex items-center gap-2 themed-heading"><Medal className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> {terms.badges}</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {data.achievements.map((a) => {
                const Icon = BADGE_ICONS[a.icon] || Award;
                return (
                  <div key={a.id} title={a.desc} data-testid={`badge-${a.id}`}
                    className="flex flex-col items-center text-center gap-1.5 p-3 rounded-2xl" style={{ background: "var(--t-surface2)", opacity: a.unlocked ? 1 : 0.5 }}>
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={a.unlocked ? { background: "var(--t-accent)", color: "var(--t-on-accent)" } : { background: "var(--t-border)", color: "var(--t-muted)" }}>
                      {a.unlocked ? <Icon className="w-6 h-6" strokeWidth={2.5} /> : <Lock className="w-5 h-5" />}
                    </div>
                    <span className="text-xs font-bold leading-tight">{a.title}</span>
                  </div>
                );
              })}
            </div>
          </ThemedCard>
        </div>

        {/* Skills (per subject) */}
        <ThemedCard className="p-6 mt-6">
          <h3 className="font-heading font-extrabold mb-4 flex items-center gap-2 themed-heading"><Target className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> {terms.skills} · {SUBJECTS[subject].label}</h3>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {sub.skills.map((s) => (
              <div key={s.skill} data-testid={`skill-${s.skill}`}>
                <div className="flex justify-between text-sm font-bold mb-1.5"><span>{s.label}</span><span style={muted}>{s.total ? `${s.accuracy}%` : "—"}</span></div>
                <ThemedProgress value={s.accuracy} fill={s.accuracy >= 70 ? "var(--t-success)" : s.accuracy >= 40 ? "var(--t-accent)" : "var(--t-primary)"} />
              </div>
            ))}
          </div>
        </ThemedCard>
      </main>
    </ThemedShell>
  );
}
